import React, { useState, useEffect } from 'react';
import { auth, calendar, coachInvites } from '../../api';
import { useTheme } from '../../contexts/ThemeContext';
import NotificationsCard from './NotificationsCard';
import SpanishAudioManager from '../Spanish/SpanishAudioManager';
import DeleteAccountCard from './DeleteAccountCard';
import IphoneCalendarCard from './IphoneCalendarCard';
import { isAvailable as deviceCalendarAvailable } from '../../utils/deviceCalendar';
import { getCachedSpanishPref, setCachedSpanishPref } from '../../utils/spanishPref';
import './SettingsPage.css';

const SettingsPage = ({ user, onLogout, onBack }) => {
  const userId = user && user._id;
  const { theme, setTheme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const canConnectCalendar = user?.role === 'coach' || user?.role === 'headcoach';
  const [gcalStatus, setGcalStatus] = useState(null); // { connected, connectedAt }
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalError, setGcalError] = useState('');

  useEffect(() => {
    if (!canConnectCalendar) return;
    calendar.getGoogleStatus()
      .then(res => setGcalStatus(res.data))
      .catch(() => setGcalStatus({ connected: false }));
  }, [canConnectCalendar]);

  const handleConnectCalendar = async () => {
    setGcalError('');
    setGcalLoading(true);
    try {
      const res = await calendar.getGoogleConnectUrl();
      window.location.href = res.data.url;
    } catch (err) {
      setGcalError(err.response?.data?.error || 'Failed to start Google Calendar connection');
      setGcalLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    setGcalError('');
    setGcalLoading(true);
    try {
      await calendar.disconnectGoogle();
      setGcalStatus({ connected: false });
    } catch (err) {
      setGcalError(err.response?.data?.error || 'Failed to disconnect Google Calendar');
    } finally {
      setGcalLoading(false);
    }
  };

  // Coach/headcoach opt-out for the "lesson starts in 15 min" briefing push
  const [briefingEnabled, setBriefingEnabled] = useState(null); // null = loading
  const [briefingSaving, setBriefingSaving] = useState(false);

  useEffect(() => {
    if (!canConnectCalendar) return;
    auth.getPreferences()
      .then(res => setBriefingEnabled(res.data.lessonBriefingEnabled !== false))
      .catch(() => setBriefingEnabled(true));
  }, [canConnectCalendar]);

  // Players: receive "your coach has open slots" pushes
  const [promoEnabled, setPromoEnabled] = useState(null); // null = loading
  const [promoSaving, setPromoSaving] = useState(false);

  useEffect(() => {
    if (canConnectCalendar) return;
    auth.getPreferences()
      .then(res => setPromoEnabled(res.data.slotPromotionsEnabled !== false))
      .catch(() => setPromoEnabled(true));
  }, [canConnectCalendar]);

  // Players: opt in to the Padel Espanol learning track
  const [spanishEnabled, setSpanishEnabled] = useState(() => getCachedSpanishPref(user && user._id)); // null = not known yet
  const [spanishSaving, setSpanishSaving] = useState(false);
  const [showSpanishAudio, setShowSpanishAudio] = useState(false);

  useEffect(() => {
    if (canConnectCalendar) return;
    auth.getPreferences()
      .then(res => { const on = res.data.spanishEnabled === true; setSpanishEnabled(on); setCachedSpanishPref(userId, on); })
      .catch(() => setSpanishEnabled(prev => (prev === null ? false : prev))); // keep the remembered choice
  }, [canConnectCalendar]);

  const handleSpanishChange = async (enabled) => {
    if (spanishSaving || enabled === spanishEnabled) return;
    const previous = spanishEnabled;
    setSpanishEnabled(enabled);
    setSpanishSaving(true);
    try {
      await auth.updatePreferences({ spanishEnabled: enabled });
      setCachedSpanishPref(userId, enabled);
      window.dispatchEvent(new CustomEvent('spanishPrefChanged', { detail: { enabled } }));
    } catch (err) {
      setSpanishEnabled(previous);
      setError('Could not save your Spanish setting. Please try again.');
    } finally {
      setSpanishSaving(false);
    }
  };

  const handlePromoChange = async (enabled) => {
    if (promoSaving || enabled === promoEnabled) return;
    const previous = promoEnabled;
    setPromoEnabled(enabled);
    setPromoSaving(true);
    try {
      await auth.updatePreferences({ slotPromotionsEnabled: enabled });
    } catch (err) {
      setPromoEnabled(previous);
      setError('Could not save your notification setting. Please try again.');
    } finally {
      setPromoSaving(false);
    }
  };

  const handleBriefingChange = async (enabled) => {
    if (briefingSaving || enabled === briefingEnabled) return;
    const previous = briefingEnabled;
    setBriefingEnabled(enabled);
    setBriefingSaving(true);
    try {
      await auth.updatePreferences({ lessonBriefingEnabled: enabled });
    } catch (err) {
      setBriefingEnabled(previous);
      setError('Could not save your notification setting. Please try again.');
    } finally {
      setBriefingSaving(false);
    }
  };

  const isHeadcoach = user?.role === 'headcoach';
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy Link');

  const loadInvites = () => {
    coachInvites.list()
      .then(res => setInvites(res.data.invites || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (isHeadcoach) loadInvites();
  }, [isHeadcoach]);

  const handleCreateInvite = async () => {
    setInviteError('');
    setInviteLoading(true);
    try {
      const res = await coachInvites.create();
      setGeneratedUrl(res.data.url);
      setCopyLabel('Copy Link');
      loadInvites();
    } catch (err) {
      setInviteError(err.response?.data?.message || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Copy Link'), 2000);
    } catch (err) {
      setCopyLabel('Copy failed');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await coachInvites.revoke(inviteId);
      loadInvites();
    } catch (err) {
      setInviteError(err.response?.data?.message || 'Failed to revoke invite');
    }
  };

  const validateForm = () => {
    // Clear previous errors
    setError('');

    // Check all fields filled
    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      setError('All fields are required');
      return false;
    }

    // Check password length
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    // Check passwords match
    if (newPassword !== newPasswordConfirm) {
      setError('New passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await auth.changePassword({
        currentPassword,
        newPassword,
        newPasswordConfirm
      });

      // Show success message
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');

      // Auto-logout after 2.5 seconds
      setTimeout(() => {
        onLogout();
      }, 2500);
    } catch (err) {
      console.error('❌ Password change error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to change password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page-container">
      <div className="settings-page-card">
        {/* Header */}
        <div className="settings-header">
          <h2>Account Settings</h2>
          {onBack && (
            <button className="back-button" onClick={onBack} title="Back">
              ← Back
            </button>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            <p>Password changed successfully! Logging you out...</p>
          </div>
        )}

        {/* Error Message */}
        {error && !success && (
          <div className="error-message">
            <span className="error-icon">❌</span>
            <p>{error}</p>
          </div>
        )}

        {/* User Email Display */}
        <div className="settings-section">
          <label>Email Address (Read-only)</label>
          <div className="email-display">
            {user?.email || 'Not available'}
          </div>
        </div>

        {/* Push status on this device + iPhone Home Screen walkthrough */}
        <NotificationsCard />

        {/* Slot promotions (players) */}
        {!canConnectCalendar && (
          <div className="settings-section">
            <label>Open slot notifications</label>
            <div className="theme-toggle" role="group" aria-label="Open slot notifications">
              <button
                type="button"
                className={`theme-toggle-option ${promoEnabled !== false ? 'active' : ''}`}
                onClick={() => handlePromoChange(true)}
                disabled={promoEnabled === null || promoSaving}
                aria-pressed={promoEnabled !== false}
              >
                🔔 On
              </button>
              <button
                type="button"
                className={`theme-toggle-option ${promoEnabled === false ? 'active' : ''}`}
                onClick={() => handlePromoChange(false)}
                disabled={promoEnabled === null || promoSaving}
                aria-pressed={promoEnabled === false}
              >
                🔕 Off
              </button>
            </div>
            <span className="password-info">
              Get a heads-up when your coach has open coaching slots.
            </span>
          </div>
        )}

        {/* Padel Espanol opt-in (players) */}
        {!canConnectCalendar && (
          <div className="settings-section">
            <label>🇪🇸 Spanish learning</label>
            <div className="theme-toggle" role="group" aria-label="Spanish learning">
              <button
                type="button"
                className={`theme-toggle-option ${spanishEnabled === true ? 'active' : ''}`}
                onClick={() => handleSpanishChange(true)}
                disabled={spanishEnabled === null || spanishSaving}
                aria-pressed={spanishEnabled === true}
              >
                On
              </button>
              <button
                type="button"
                className={`theme-toggle-option ${spanishEnabled !== true ? 'active' : ''}`}
                onClick={() => handleSpanishChange(false)}
                disabled={spanishEnabled === null || spanishSaving}
                aria-pressed={spanishEnabled !== true}
              >
                Off
              </button>
            </div>
            <span className="password-info">
              Learn Spanish through padel: words from your lessons, daily practice, and progress your coach can see. Adds a Spanish tab.
            </span>
          </div>
        )}

        {/* Spanish audio (coach / headcoach only) */}
        {canConnectCalendar && (
          <div className="settings-section">
            <label>🇪🇸 Spanish audio</label>
            <button type="button" className="submit-button" style={{ padding: '10px 18px' }} onClick={() => setShowSpanishAudio(true)}>
              🎙️ Record Spanish words
            </button>
            <span className="password-info">
              Record how each Spanish word sounds. Students hear your voice when they tap 🔊.
            </span>
          </div>
        )}
        {showSpanishAudio && <SpanishAudioManager onClose={() => setShowSpanishAudio(false)} />}

        {/* Lesson briefing notification (coach / headcoach only) */}
        {canConnectCalendar && (
          <div className="settings-section">
            <label>Lesson briefing notification</label>
            <div className="theme-toggle" role="group" aria-label="Lesson briefing notification">
              <button
                type="button"
                className={`theme-toggle-option ${briefingEnabled !== false ? 'active' : ''}`}
                onClick={() => handleBriefingChange(true)}
                disabled={briefingEnabled === null || briefingSaving}
                aria-pressed={briefingEnabled !== false}
              >
                🔔 On
              </button>
              <button
                type="button"
                className={`theme-toggle-option ${briefingEnabled === false ? 'active' : ''}`}
                onClick={() => handleBriefingChange(false)}
                disabled={briefingEnabled === null || briefingSaving}
                aria-pressed={briefingEnabled === false}
              >
                🔕 Off
              </button>
            </div>
            <span className="password-info">
              15 minutes before each lesson, get a reminder with your last note on that student and the skill points still to unlock (matched from the calendar event title).
            </span>
          </div>
        )}

        {/* Appearance */}
        <div className="settings-section">
          <label>Appearance</label>
          <div className="theme-toggle" role="group" aria-label="Theme">
            <button
              type="button"
              className={`theme-toggle-option ${theme === 'light' ? 'active' : ''}`}
              onClick={() => setTheme('light')}
              aria-pressed={theme === 'light'}
            >
              ☀️ Light
            </button>
            <button
              type="button"
              className={`theme-toggle-option ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => setTheme('dark')}
              aria-pressed={theme === 'dark'}
            >
              🌙 Dark
            </button>
          </div>
        </div>

        {/* Google Calendar Connection */}
        {canConnectCalendar && (
          <div className="settings-section">
            <label>Google Calendar</label>
            {gcalError && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <p>{gcalError}</p>
              </div>
            )}
            {gcalStatus?.connected ? (
              <>
                <p className="password-info">
                  ✅ Connected{gcalStatus.connectedAt
                    ? ` on ${new Date(gcalStatus.connectedAt).toLocaleDateString()}`
                    : ''}. Your own calendar is used for bookings and buffer detection.
                </p>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleDisconnectCalendar}
                  disabled={gcalLoading}
                >
                  {gcalLoading ? 'Disconnecting...' : 'Disconnect Google Calendar'}
                </button>
              </>
            ) : (
              <>
                <p className="password-info">
                  Not connected. Without this, your bookings use one calendar shared across all coaches.
                </p>
                <button
                  type="button"
                  className="submit-button"
                  onClick={handleConnectCalendar}
                  disabled={gcalLoading}
                >
                  {gcalLoading ? 'Redirecting...' : '📅 Connect Google Calendar'}
                </button>
              </>
            )}
          </div>
        )}

        {/* iPhone's own calendar (iOS app only) - link it so managers and the headcoach can see it */}
        {canConnectCalendar && deviceCalendarAvailable() && <IphoneCalendarCard />}


        {/* Invite a Coach (headcoach only - the only way a coach account gets created) */}
        {isHeadcoach && (
          <div className="settings-section">
            <label>Invite a Coach</label>
            {inviteError && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <p>{inviteError}</p>
              </div>
            )}
            <p className="password-info">
              Generate a one-time link to invite a new coach. They'll be assigned to you automatically.
            </p>
            <button
              type="button"
              className="submit-button"
              onClick={handleCreateInvite}
              disabled={inviteLoading}
            >
              {inviteLoading ? 'Generating...' : '➕ Generate Invite Link'}
            </button>

            {generatedUrl && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
                <input
                  type="text"
                  readOnly
                  value={generatedUrl}
                  onClick={(e) => e.target.select()}
                  style={{ flex: 1, fontSize: '12px' }}
                />
                <button type="button" className="cancel-button" onClick={handleCopyInviteLink}>
                  {copyLabel}
                </button>
              </div>
            )}

            {invites.length > 0 && (
              <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {invites.map(invite => (
                  <div
                    key={invite._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '13px',
                      padding: '8px 10px',
                      background: 'rgba(0,0,0,0.03)',
                      borderRadius: '6px'
                    }}
                  >
                    <span>
                      {invite.status === 'used' && `✅ Used by ${invite.usedBy?.name || invite.usedBy?.email || 'a coach'}`}
                      {invite.status === 'pending' && `⏳ Pending (expires ${new Date(invite.expiresAt).toLocaleDateString()})`}
                      {invite.status === 'expired' && '⌛ Expired'}
                      {invite.status === 'revoked' && '🚫 Revoked'}
                    </span>
                    {invite.status === 'pending' && (
                      <button
                        type="button"
                        className="cancel-button"
                        style={{ padding: '4px 10px', fontSize: '12px' }}
                        onClick={() => handleRevokeInvite(invite._id)}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Password Change Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="password-form">
            <div className="settings-section">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                disabled={loading}
              />
            </div>

            <div className="settings-section">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                disabled={loading}
              />
              <span className="password-info">
                {newPassword.length > 0
                  ? `${newPassword.length} character${newPassword.length !== 1 ? 's' : ''}`
                  : 'Minimum 6 characters required'}
              </span>
            </div>

            <div className="settings-section">
              <label htmlFor="newPasswordConfirm">Confirm New Password</label>
              <input
                id="newPasswordConfirm"
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="Confirm your new password"
                disabled={loading}
              />
              {newPassword && newPasswordConfirm && (
                <span className={`password-match ${newPassword === newPasswordConfirm ? 'match' : 'no-match'}`}>
                  {newPassword === newPasswordConfirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                </span>
              )}
            </div>

            {/* Buttons */}
            <div className="settings-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={loading || !currentPassword || !newPassword || !newPasswordConfirm}
              >
                {loading ? 'Updating...' : 'Change Password'}
              </button>
              {onBack && (
                <button
                  type="button"
                  className="cancel-button"
                  onClick={onBack}
                  disabled={loading}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}

        {onLogout && (
          <div className="settings-section settings-logout">
            <button type="button" className="settings-logout-btn" onClick={onLogout}>Log out</button>
          </div>
        )}

        {/* Privacy policy + account deletion (store requirement) */}
        <DeleteAccountCard user={user} onDeleted={onLogout} />
      </div>
    </div>
  );
};

export default SettingsPage;
